import styled from "@emotion/styled";
import { Badge } from "./element/Badge";
import SmileyPersonIcon from "../../../img/home/smiley-person.svg?react";
import HeartIcon from "../../../img/home/heart.svg?react";
import LoudSpeakerIcon from "../../../img/home/loud-speaker.svg?react";
import MedalIcon from "../../../img/home/medal.svg?react";
import LightbulbIcon from "../../../img/home/lightbulb.svg?react";
import DiscordIcon from "../../../img/discord.svg?react";
import communityMerchImgSrc from "../../../img/home/community.png";
import communityMerchMobileImgSrc from "../../../img/home/community-mobile.png";
import { MOBILE_WIDTH, TABLET_WIDTH, useIsMobile } from "hooks/useDeviceType";
import { css } from "@emotion/react";
import { Title } from "./element/Title";
import { Text } from "components/general/Text";
import { Spacer } from "components/general/Spacer/Spacer";
import { useWindowSize } from "usehooks-ts";
import { times } from "lodash-es";
import { GlobalStore } from "store/global/GlobalStore";
import { Link } from "react-router-dom";
import { NAV_LINKS } from "config/consts";

export const FeatureCommunity = () => {
  const isMobile = useIsMobile();
  return (
    <section>
      <TriangleBorder position="top" />
      <Container>
        <Badge icon={SmileyPersonIcon} text="Community" />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? 40 : 56,
            alignItems: "center",
          }}
        >
          <div style={{ padding: `0 ${isMobile ? 18 : 73}px` }}>
            <Title
              css={css`
                margin-top: 28px;
                margin-bottom: 24px;

                @media (max-width: ${MOBILE_WIDTH}px) {
                  margin-top: 12px;
                  margin-bottom: 16px;
                }
              `}
            >
              Be the first to know, steer the discourse, rise in contests, and savour your elite
              perks
            </Title>
            <Text
              textStyle="Paragraph"
              size="Mega"
              textCss={css`
                text-align: center;
                padding: 0 24px;
              `}
            >
              Join us as a proud #bebopper and help shape the present and the future of your
              favourite trading app.
            </Text>
          </div>

          <img
            src={isMobile ? communityMerchMobileImgSrc : communityMerchImgSrc}
            alt="Merchandise and Discord feedback"
            width={isMobile ? "100%" : "80%"}
          />

          <Benefits />
        </div>
        <ContributoorsClub />
      </Container>
      <TriangleBorder position="bottom" />
    </section>
  );
};

// Sub-components

const TriangleBorder = ({ position }: { position: "top" | "bottom" }) => {
  const theme = GlobalStore.useState(s => s.theme);
  const isMobile = useIsMobile();
  const windowWidth = useWindowSize().width;
  const triangleWidth = isMobile ? 32 : 64;
  const triangleHeight = isMobile ? 16 : 32;
  const triangleCount = Math.ceil(windowWidth / triangleWidth);

  return (
    <div
      style={{
        display: "flex",
        position: "relative",
        ...(position === "top" ? { top: 1 } : { bottom: 1 }),
      }}
    >
      {times(triangleCount, i => (
        <div
          key={i}
          style={{
            backgroundColor: theme["Brand colours"].Pink,
            width: triangleWidth,
            height: triangleHeight,
            clipPath:
              position === "top"
                ? "polygon(0% 100%, 100% 100%, 50% 0%)"
                : "polygon(0% 0%, 100% 0%, 50% 100%)",
          }}
        />
      ))}
    </div>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 56px 0 88px;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.theme["Brand colours"].Pink};
  color: ${props => props.theme.button.secondary.text.default};

  @media (max-width: ${MOBILE_WIDTH}px) {
    padding: 32px 0;
  },
`;

const Benefits = () => {
  return (
    <BenefitsContainer>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <LightbulbIcon />
        <Spacer minHeight={16} />
        <Text textStyle="Paragraph" size="XXL">
          Share your feedback
          <br />
          and ideas
        </Text>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <LoudSpeakerIcon />
        <Spacer minHeight={16} />
        <Text textStyle="Paragraph" size="XXL">
          Spread the word across
          <br />
          the community
        </Text>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <HeartIcon />
        <Spacer minHeight={16} />
        <Text textStyle="Paragraph" size="XXL">
          Bring good vibes
          <br />
          and memes
        </Text>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <MedalIcon />
        <Spacer minHeight={16} />
        <Text textStyle="Paragraph" size="XXL">
          Get vanity rewards
        </Text>
      </div>
    </BenefitsContainer>
  );
};

const BenefitsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 46px;
  text-align: center;

  @media (max-width: ${MOBILE_WIDTH + 240}px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const ContributoorsClub = () => {
  return (
    <ContributoorsClubContainer>
      <Title
        css={css`
          font-size: 40px;
          font-weight: 800;
          line-height: 111%;
          @media (max-width: ${TABLET_WIDTH}px) {
            font-size: 40px;
          }
          @media (max-width: ${MOBILE_WIDTH}px) {
            font-size: 30px;
          }
        `}
      >
        Contributoors Club
      </Title>
      <Text textStyle="Numbers" size="XL" shouldScale={true}>
        Our invitation-only club for the top community members offers special status and perks.
      </Text>
      <JoinDiscordButton to={NAV_LINKS.DISCORD} target="_blank" rel="noopener noreferrer">
        <DiscordIcon />
        <Text textStyle="Button" size="Extra Large" shouldScale={true}>
          Join Discord
        </Text>
      </JoinDiscordButton>
    </ContributoorsClubContainer>
  );
};

const ContributoorsClubContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 597px;
  padding: 40px;
  align-items: center;
  gap: 24px;
  margin: 84px 18px 0;
  border-radius: 36px;
  background-color: ${props => props.theme.theme[1700]};
  text-align: center;

  @media (max-width: ${MOBILE_WIDTH}px) {
    margin-top: 64px;
  }
`;

const JoinDiscordButton = styled(Link)`
  border-radius: 24px;
  background-color: #5865f2;
  color: ${props => props.theme.font.primary.default};
  display: flex;
  gap: 10px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  transition: background-color 0.2s linear;

  &:hover {
    background-color: #6572f7;
  }
`;
